import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import usePageMeta from "@/hooks/usePageMeta";

const stats = [
  { value: "11", label: "收录影视作品", note: "电影 7 部 · 剧集 4 部" },
  { value: "7", label: "深度影评文章", note: "覆盖 5 个专题方向" },
  { value: "6", label: "覆盖地区", note: "中国大陆 / 港台 / 日韩 / 英国" },
  { value: "3", label: "分析维度", note: "剧情 · 角色 · 影评" },
];

const coverage = [
  {
    icon: "ri-book-open-line",
    title: "剧情介绍",
    description:
      "不做流水账式复述。每一部作品的剧情页都会标出关键转折与人物选择的时刻，让读者在阅读时就能感知到伦理张力的位置。",
  },
  {
    icon: "ri-team-line",
    title: "角色分析",
    description:
      "我们关心人物为什么只能这样选择。角色卡片会标注其动机、局限与所处的关系网络，帮助理解「坏人」与「好人」之间那片灰色地带。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "影评与解析",
    description:
      "从叙事结构、镜头语言到伦理立场，编辑部长文会给出明确的观点，也会保留反对意见，不做模棱两可的推荐。",
  },
];

const principles = [
  {
    title: "不替观众下判断",
    description: "伦理题材的价值在于让人犹豫。我们呈现理由，但把判断留给读者。",
  },
  {
    title: "区分作品与人",
    description: "分析角色时，避免把虚构人物的选择等同于演员或创作者本人的立场。",
  },
  {
    title: "尊重现实处境",
    description: "任何道德讨论都应考虑资源、阶层与性别带来的现实约束，而不是抽象地说教。",
  },
  {
    title: "标注内容来源",
    description: "站内演示内容的性质会在页面中明确说明，不虚构评分与用户评论。",
  },
];

const timeline = [
  { year: "2021", title: "从一个片单开始", description: "最初只是编辑部内部传阅的十二部片单，附带手写的观后笔记。" },
  { year: "2022", title: "建立作品资料库", description: "把剧情、人物与影评统一到一个结构里，形成了现在的详情页雏形。" },
  { year: "2023", title: "影评专题上线", description: "开始按家庭、性别、悬疑等议题组织长文，让讨论不再依附于单部作品。" },
  { year: "2024", title: "门户化改版", description: "重构检索与筛选，按地区、年代、评分帮助读者更快找到想看的作品。" },
];

const team = [
  {
    name: "沈砚秋",
    role: "主编 · 影评",
    bio: "关注家庭伦理与代际关系，相信好的影评应该先描述，再判断。",
    avatar:
      "https://readdy.ai/api/search-image?query=natural%20portrait%20of%20a%20thoughtful%20east%20asian%20woman%20in%20her%20thirties%20wearing%20a%20simple%20navy%20shirt%2C%20soft%20window%20light%2C%20warm%20neutral%20background%2C%20editorial%20headshot%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=400&height=400&seq=team-shen&orientation=squarish",
  },
  {
    name: "何嘉言",
    role: "剧集解析",
    bio: "长期跟踪亚洲家庭剧，擅长从空间与日常细节切入分析。",
    avatar:
      "https://readdy.ai/api/search-image?query=natural%20portrait%20of%20a%20calm%20east%20asian%20man%20in%20his%20forties%20wearing%20a%20grey%20knit%20sweater%2C%20soft%20indoor%20light%2C%20warm%20neutral%20background%2C%20editorial%20headshot%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=400&height=400&seq=team-he&orientation=squarish",
  },
  {
    name: "周慕之",
    role: "悬疑与犯罪",
    bio: "关注真相叙事背后的伦理负债，认为悬疑的核心永远是人。",
    avatar:
      "https://readdy.ai/api/search-image?query=natural%20portrait%20of%20a%20young%20east%20asian%20man%20with%20glasses%20in%20his%20late%20twenties%2C%20black%20shirt%2C%20soft%20window%20light%2C%20warm%20neutral%20background%2C%20editorial%20headshot%2C%20no%20text&width=400&height=400&seq=team-zhou&orientation=squarish",
  },
  {
    name: "苏禾",
    role: "性别议题",
    bio: "研究者与写作者，关注亲密关系中的隐形权力结构。",
    avatar:
      "https://readdy.ai/api/search-image?query=natural%20portrait%20of%20an%20east%20asian%20woman%20in%20her%20early%20thirties%20with%20short%20hair%2C%20cream%20colored%20top%2C%20soft%20neutral%20studio%20light%2C%20warm%20beige%20background%2C%20editorial%20headshot%2C%20no%20text&width=400&height=400&seq=team-su&orientation=squarish",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 伦理视频",
    "了解伦理视频的网站定位、内容覆盖范围与编辑原则。我们以剧情、角色与影评三个维度，记录伦理题材影视作品中的选择与代价。",
    "关于伦理视频,伦理影视内容站,影视分析团队,编辑原则,伦理视频介绍",
  );

  return (
    <SiteLayout>
      <PageHero
        eyebrow="About Ethics Cinema"
        title="关于伦理视频"
        description="一个专注伦理题材影视作品的内容门户。我们相信，好的伦理故事不提供答案，只把问题留在观众心里更久一点。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full px-4 py-12 md:px-10 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">Who We Are</span>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              我们是谁
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-[1.95] text-foreground-700">
              <p>
                伦理视频是一个由影视爱好者与写作者共同维护的内容站点。我们不生产影视内容，也不提供在线播放，只做一件事：把伦理题材作品讲清楚。
              </p>
              <p>
                这里的「伦理」并非标签化的猎奇，而是指那些真正处理道德困境的作品——它们关于家庭、亲密关系、职业责任、公共正义，关于一个人在被逼到角落时仍然要做的选择。
              </p>
              <p>
                我们为每部作品建立完整的资料页：剧情梳理、角色动机、独立影评，以及基于类型与导演的相似推荐。你可以把这里当作一个可以慢慢读的片单。
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/movies"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                浏览伦理电影
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/reviews"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                阅读影评专题
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg bg-background-200">
              <div className="aspect-[4/3] w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=quiet%20editorial%20still%20of%20a%20small%20film%20screening%20room%20with%20wooden%20chairs%20and%20a%20warm%20amber%20projector%20glow%2C%20deep%20navy%20walls%2C%20stacked%20film%20books%20on%20a%20side%20table%2C%20soft%20grain%2C%20cinematic%20atmosphere%2C%20no%20text&width=1000&height=750&seq=about-studio&orientation=landscape"
                  alt="伦理视频编辑部观影空间"
                  title="伦理视频 - 编辑部观影空间"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              <div className="bg-primary-900 p-5">
                <p className="font-heading text-sm leading-relaxed text-background-100">
                  「先看完，再说话。这是我们给自己定的第一条规矩。」
                </p>
                <p className="mt-2 text-[11px] text-background-400">伦理视频 · 编辑部</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-10 md:px-10 md:py-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="font-heading text-3xl font-semibold text-primary-600 md:text-4xl">{stat.value}</p>
              <p className="mt-2 text-xs font-medium text-foreground-900 md:text-sm">{stat.label}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{stat.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-10 md:py-16">
        <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">Coverage</span>
        <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">内容覆盖范围</h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">
          从单部作品的资料整理，到跨作品的议题讨论，站内内容分为三个层次。
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {coverage.map((item, index) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <span className="font-heading text-sm text-foreground-300">0{index + 1}</span>
              </div>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-primary-950 px-4 py-12 md:px-10 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-400">Principles</span>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-background-50 md:text-3xl">编辑原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-background-300">
              伦理讨论很容易变成道德审判。以下四条，是我们写每一篇文章前都会过一遍的自检清单。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2 lg:col-span-8">
            {principles.map((item, index) => (
              <div key={item.title} className="border-t border-background-50/15 pt-4">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-xs text-accent-400">0{index + 1}</span>
                  <h3 className="font-heading text-sm font-medium text-background-50 md:text-base">{item.title}</h3>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-background-300 md:text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-10 md:py-16">
        <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">Timeline</span>
        <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">我们走到这里</h2>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-4">
          {timeline.map((item) => (
            <div key={item.year} className="relative border-l-2 border-background-200 pl-4 md:border-l-0 md:border-t-2 md:pl-0 md:pt-4">
              <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-accent-500 md:-top-[7px] md:left-0" />
              <p className="font-heading text-xl font-semibold text-primary-600">{item.year}</p>
              <h3 className="mt-1.5 font-heading text-sm font-medium text-foreground-950">{item.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-12 md:px-10 md:py-16">
        <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">Editorial Team</span>
        <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">编辑部</h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">
          四个人，四种偏好。争执通常发生在截稿前一小时。
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {team.map((member) => (
            <div key={member.name} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-[4/3] w-full overflow-hidden bg-background-200">
                <img
                  src={member.avatar}
                  alt={`${member.name}头像`}
                  title={`${member.name} - 伦理视频编辑部`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-sm font-semibold text-foreground-950">{member.name}</h3>
                <p className="mt-0.5 text-[11px] text-accent-700">{member.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-10 md:py-16">
        <div className="flex flex-col items-start justify-between gap-5 rounded-lg bg-primary-900 p-6 md:flex-row md:items-center md:p-9">
          <div>
            <h2 className="font-heading text-xl font-semibold text-background-50 md:text-2xl">
              有想推荐的作品，或不同的看法？
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-300">
              我们一直在寻找值得被认真讨论的伦理题材作品。写信告诉我们片名，也欢迎指出文章里的错误。
            </p>
          </div>
          <Link
            to="/contact"
            className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
          >
            联系我们
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}